import { CurrencyRupee, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Checkbox, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Button, Input } from "../../../components";
import cn from "../../../utils/cn";
import indianNumberFormatter from "../../../utils/indian-number-formatter";
import styles from "../ngo-org-page/Campaign-ngo.module.css";
import { mobileText } from "../../../utils/responsiveStyle";

type Project = {
  _id: string;
  name: string;
  goal?: number | string;
};

type SelectedProject = {
  _id: string;
  value: string;
};

type SelectedNGO = {
  _id: string;
  projects?: SelectedProject[];
};

type DonationItem =
  | {
      _id: string;
      name: string;
      isGeneral: false;
      goal?: number | string;
    }
  | {
      _id: string;
      name: string;
      isGeneral: true;
    };

export interface NGOCardProps_V2 {
  selectedNGOS: SelectedNGO[];
  ngo: {
    _id: string;
    name: string;
    logo?: { url: string };
    category?: { name: string };
    about: string;
    projects: Project[];
  };
  onCheckboxChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string,
  ) => void;
  onViewMoreClick: () => void;
  isVoting: boolean;
  errors: Record<string, string>;
  onProjectToggle?: (
    ngoId: string,
    projectId: string,
    checked: boolean,
  ) => void;
  onProjectValueChange?: (
    ngoId: string,
    projectId: string,
    value: string,
  ) => void;
  organization: any;
  closeProjectModel: boolean;
  isForSingleNGO?: boolean;
}

const GENERAL_DONATION_ID = "general";
const GENERAL_DONATION_NAME = "Others";

const NGOCard_V2: React.FC<NGOCardProps_V2> = React.memo(
  ({
    selectedNGOS,
    ngo,
    onCheckboxChange,
    onViewMoreClick,
    isVoting,
    errors,
    onProjectToggle,
    onProjectValueChange,
    closeProjectModel,
    isForSingleNGO = false,
  }) => {
    const { _id, name, logo, category, about, projects } = ngo;

    const includedNGO = selectedNGOS.find((n) => n._id === _id);
    const isNGOSelected = !!includedNGO;

    const [ngoProjects] = useState<Project[]>(projects ?? []);
    const [projectsOpen, setProjectsOpen] = useState(false);

    const handleToggleProjects = useCallback(() => {
      setProjectsOpen((prev) => !prev);
    }, []);

    useEffect(() => {
      setProjectsOpen(false);
    }, [closeProjectModel]);

    // ── Prepare full list: real projects + general donation ──
    const allDonationItems: DonationItem[] = [
      ...ngoProjects.map((p) => ({
        _id: p._id,
        name: p.name,
        goal: p.goal,
        isGeneral: false as const,
      })),
      {
        _id: GENERAL_DONATION_ID,
        name: GENERAL_DONATION_NAME,
        isGeneral: true as const,
      },
    ];

    const totalAmount = React.useMemo(() => {
      if (!includedNGO?.projects?.length) return 0;
      return includedNGO.projects.reduce((sum, proj) => {
        const clean = proj.value?.replace(/,/g, "") || "0";
        return sum + (Number(clean) || 0);
      }, 0);
    }, [includedNGO?.projects]);

    const formattedTotal = indianNumberFormatter(totalAmount.toString());

    return (
      <div className={cn(styles.card, includedNGO ? styles.selectedCard : "")}>
        <div className={styles.ngoCard}>
          <div className={styles.ngoDetails}>
            <div className={styles.ngoDetailsLeft}>
              <Box className={styles.checkBoxContainer}>
                <Checkbox
                  sx={{ p: 0 }}
                  checked={isNGOSelected}
                  onChange={(e) => onCheckboxChange(e, _id)}
                />
              </Box>
              <Typography sx={{ ...mobileText, fontWeight: 600 }}>
                {name?.toUpperCase()}
              </Typography>
              <p className={styles.ngoDescription}>{about}</p>

              {!isForSingleNGO && (
                <Button
                  variant="text"
                  className={styles.viewMoreBtn}
                  onClick={onViewMoreClick}
                >
                  View More
                </Button>
              )}
            </div>

            <div>
              {logo?.url && (
                <img
                  alt="NGO logo"
                  src={logo.url}
                  className={styles.ngoImage}
                />
              )}
            </div>
          </div>
        </div>

        {!isVoting && isNGOSelected && (
          <Box sx={{ mt: 2, px: 2, pb: 2 }}>
            {/* Always show "Our Impact Projects" header even if only general exists */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                py: 1,
              }}
              onClick={handleToggleProjects}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="primary.main"
              >
                Select a Project to Donate
              </Typography>
              {projectsOpen ? <ExpandLess /> : <ExpandMore />}
            </Box>

            {(projectsOpen || isForSingleNGO) && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {allDonationItems.map((item) => {
                  const isGeneral = item.isGeneral;
                  const selected = includedNGO?.projects?.find(
                    (p) => p._id === item._id,
                  );

                  return (
                    <Box
                      key={item._id}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <Checkbox
                          checked={!!selected}
                          onChange={(e) =>
                            onProjectToggle?.(_id, item._id, e.target.checked)
                          }
                        />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Typography fontWeight={600}>{item.name}</Typography>
                          <Typography fontSize={13}>
                            {!item.isGeneral && (
                              <Typography fontSize={13}>
                                Budget: ₹
                                {indianNumberFormatter(String(item.goal ?? 0))}
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      </Box>

                      {selected && (
                        <Input
                          fullWidth
                          value={selected.value}
                          label={
                            isGeneral
                              ? "Amount for General Donation"
                              : "Enter Amount for this Project"
                          }
                          placeholder="Type the amount"
                          slotProps={{
                            input: {
                              startAdornment: (
                                <CurrencyRupee sx={{ fontSize: 18 }} />
                              ),
                            },
                          }}
                          onChange={(e) =>
                            onProjectValueChange?.(
                              _id,
                              item._id,
                              e.target.value,
                            )
                          }
                          error={!!errors[`${_id}-${item._id}`]}
                          helperText={errors[`${_id}-${item._id}`]}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        <div className={styles.donateCard}>
          {includedNGO && totalAmount > 0 && (
            <Box sx={{ p: 2, backgroundColor: "#f8f9fa", borderRadius: 1 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Total Pledged Amount
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <CurrencyRupee sx={{ fontSize: 28, color: "primary.main" }} />
                <Typography variant="h5" fontWeight="bold" color="primary.dark">
                  ₹{formattedTotal}
                </Typography>
              </Box>
            </Box>
          )}
        </div>

        <span className={styles.sectorBadge}>
          {String(category?.name ?? "")}
        </span>
      </div>
    );
  },
);

export default NGOCard_V2;
